import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  favoriteTypeConfig,
  favoriteStats,
  allFavorites,
  quickAccessFavorites,
  favoriteRules,
  getFavoritesByType,
  getOnAirFavorites,
  type FavoriteType,
  type FavoriteItem,
  type FavoriteRadio,
  type FavoritePrograma,
  type FavoriteLocutor,
  type FavoriteEvento,
  type FavoriteConcurso,
  type FavoriteClub,
} from '@/mocks/rfwFavorites';

function TypeBadge({ type }: { type: FavoriteType }) {
  const c = favoriteTypeConfig[type];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold ${c.bgColor} ${c.color} border border-current/20`}>
      <i className={`${c.icon} text-[8px]`}></i>
      {c.label}
    </span>
  );
}

export default function FavoritosSection() {
  const [activeTab, setActiveTab] = useState<'favoritos' | 'acceso-rapido' | 'reglas'>('favoritos');
  const [typeFilter, setTypeFilter] = useState<FavoriteType | 'todas'>('todas');

  const filtered = getFavoritesByType(typeFilter);
  const onAir = getOnAirFavorites();

  const tabs = [
    { id: 'favoritos' as const, label: 'Mis Favoritos', icon: 'ri-heart-3-line', badge: favoriteStats.totalFavorites },
    { id: 'acceso-rapido' as const, label: 'Acceso Rápido', icon: 'ri-flashlight-line', badge: favoriteStats.quickAccessItems },
    { id: 'reglas' as const, label: 'Reglas', icon: 'ri-scales-line', badge: 3 },
  ];

  const typeFilters: { id: FavoriteType | 'todas'; icon: string; label: string }[] = [
    { id: 'todas', icon: 'ri-list-check-3', label: 'Todos' },
    ...Object.entries(favoriteTypeConfig).map(([key, c]) => ({
      id: key as FavoriteType,
      icon: c.icon,
      label: c.labelPlural,
    })),
  ];

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-rose-100 text-rose-700 text-xs font-semibold mb-4">
            <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"></span>
            SISTEMA DE FAVORITOS
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Todo lo que te gusta, a <span className="text-rose-600">un clic</span>
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            Seis tipos de contenido favorito en un solo lugar. Radios para escuchar, programas para no perderte, locutores que admirás, eventos que te importan, concursos en los que participás y clubes donde estás. Todos a máximo un clic de distancia.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-10">
          {[
            { label: 'Total Favoritos', value: favoriteStats.totalFavorites.toString(), icon: 'ri-heart-3-line', color: 'text-rose-600 bg-rose-50' },
            { label: 'Radios', value: favoriteStats.porRadio.toString(), icon: 'ri-radio-line', color: 'text-rose-600 bg-rose-50' },
            { label: 'Programas', value: favoriteStats.porPrograma.toString(), icon: 'ri-calendar-check-line', color: 'text-violet-600 bg-violet-50' },
            { label: 'Locutores', value: favoriteStats.porLocutor.toString(), icon: 'ri-mic-line', color: 'text-amber-600 bg-amber-50' },
            { label: 'Eventos', value: favoriteStats.porEvento.toString(), icon: 'ri-calendar-event-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Concursos', value: favoriteStats.porConcurso.toString(), icon: 'ri-trophy-line', color: 'text-sky-600 bg-sky-50' },
            { label: 'Clubes', value: favoriteStats.porClub.toString(), icon: 'ri-group-line', color: 'text-orange-600 bg-orange-50' },
            { label: 'Al Aire Ahora', value: favoriteStats.onAirNow.toString(), icon: 'ri-broadcast-line', color: 'text-emerald-600 bg-emerald-50' },
          ].map((stat, i) => (
            <div key={i} className={`flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70 ${i === 7 ? 'lg:col-span-2' : ''}`}>
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-center mb-6">
          <div className="inline-flex bg-background-50 rounded-full p-1 border border-background-200/70">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-primary-500 text-white shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
                {tab.badge > 0 && (
                  <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[9px] font-bold ${
                    activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-rose-100 text-rose-600'
                  }`}>
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {activeTab === 'favoritos' && (
          <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-1.5 justify-center">
              {typeFilters.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setTypeFilter(f.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                    typeFilter === f.id
                      ? 'bg-primary-500 text-white border-primary-500'
                      : 'bg-background-50 text-foreground-500 border-background-200 hover:border-foreground-300'
                  }`}
                >
                  <i className={`${f.icon} text-[9px]`}></i>
                  {f.label}
                </button>
              ))}
            </div>

            {onAir.length > 0 && (
              <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-emerald-50 border border-emerald-200/60">
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                  <span className="text-xs font-semibold text-emerald-700 whitespace-nowrap">Al aire ahora:</span>
                </div>
                <div className="flex items-center gap-2 overflow-x-auto">
                  {onAir.slice(0, 5).map((item) => (
                    <span key={item.id} className="flex items-center gap-1 text-[10px] text-emerald-800 whitespace-nowrap">
                      <i className={`${favoriteTypeConfig[item.favoriteType].icon} text-[9px]`}></i>
                      {'name' in item ? item.name : ''}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.slice(0, 9).map((item) => (
                <FavoriteCard key={item.id} item={item} />
              ))}
            </div>
          </div>
        )}

        {activeTab === 'acceso-rapido' && (
          <div className="space-y-6">
            <p className="text-center text-sm text-foreground-500 max-w-xl mx-auto">
              Tus 8 favoritos principales, siempre a la vista. Ordenados por relevancia para que llegues en un solo clic a lo que más te importa.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {quickAccessFavorites.map((item) => (
                <Link
                  key={item.id}
                  to="/favoritos"
                  className="p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-rose-300/60 transition-all duration-200 group text-center"
                >
                  <div className="relative mx-auto mb-3 w-14 h-14">
                    <img
                      src={'image' in item ? item.image as string : ('photo' in item ? (item as FavoriteLocutor).photo : '')}
                      alt={'name' in item ? item.name : ''}
                      className="w-14 h-14 rounded-xl object-cover"
                    />
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-background-50 border border-background-200 flex items-center justify-center">
                      <i className={`${favoriteTypeConfig[item.favoriteType].icon} text-[10px] ${favoriteTypeConfig[item.favoriteType].color}`}></i>
                    </div>
                  </div>
                  <p className="text-xs font-bold text-foreground-950 line-clamp-2 mb-1">{'name' in item ? item.name : ''}</p>
                  <TypeBadge type={item.favoriteType} />
                </Link>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'reglas' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {favoriteRules.map((rule) => (
                <div key={rule.id} className={`p-5 rounded-2xl ${rule.bgColor} border border-background-200/50 text-center`}>
                  <div className="w-10 h-10 rounded-full bg-background-50 flex items-center justify-center mx-auto mb-2">
                    <i className={`${rule.icon} text-lg ${rule.color}`}></i>
                  </div>
                  <p className="text-sm font-bold text-foreground-950 mb-1">{rule.title}</p>
                  <p className="text-[11px] text-foreground-500">{rule.description}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {Object.entries(favoriteTypeConfig).map(([key, c]) => {
                const count = getFavoritesByType(key as FavoriteType).length;
                return (
                  <div key={key} className="p-3 rounded-xl bg-background-50 border border-background-200/70 text-center">
                    <div className={`w-8 h-8 rounded-full ${c.bgColor} flex items-center justify-center mx-auto mb-1.5`}>
                      <i className={`${c.icon} text-sm ${c.color}`}></i>
                    </div>
                    <p className="text-[10px] font-semibold text-foreground-700">{c.labelPlural}</p>
                    <p className="text-lg font-bold text-foreground-950">{count}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="text-center mt-10">
          <Link
            to="/favoritos"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap"
          >
            <i className="ri-heart-3-line"></i>
            Ver Todos Mis Favoritos
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="mt-10 p-6 rounded-2xl bg-rose-100/70 border border-rose-200/50 text-center">
          <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-cursor-line text-lg text-white"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">
            Máximo 1 clic para acceder.
          </p>
          <p className="text-xs text-foreground-600 max-w-lg mx-auto">
            En RFW tus favoritos están siempre a mano. No importa desde dónde entres — tu perfil, la landing, la app — tus radios, programas, locutores, eventos, concursos y clubes favoritos te esperan a un solo clic. Porque lo que te gusta no debería estar escondido: debería estar a la vista, siempre accesible, siempre rápido.
          </p>
        </div>
      </div>
    </section>
  );
}

function FavoriteCard({ item }: { item: FavoriteItem }) {
  const tc = favoriteTypeConfig[item.favoriteType];

  const imageUrl =
    item.favoriteType === 'locutor'
      ? (item as FavoriteLocutor).photo
      : 'image' in item ? (item as FavoriteRadio | FavoritePrograma | FavoriteEvento | FavoriteConcurso | FavoriteClub).image : '';

  const itemName = 'name' in item ? item.name : '';
  const subtitle = getSubtitle(item);

  return (
    <Link
      to="/favoritos"
      className="p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-rose-300/60 transition-all duration-200 group"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="relative shrink-0">
          <img src={imageUrl} alt={itemName} className="w-12 h-12 rounded-xl object-cover" />
          <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full ${tc.bgColor} flex items-center justify-center border-2 border-background-50`}>
            <i className={`${tc.icon} text-[9px] ${tc.color}`}></i>
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold text-foreground-950 line-clamp-1">{itemName}</p>
          <p className="text-[10px] text-foreground-500 line-clamp-1">{subtitle}</p>
          <div className="flex items-center gap-1.5 mt-1">
            <TypeBadge type={item.favoriteType} />
            {isOnAir(item) && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-emerald-100 text-emerald-700">
                <span className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse"></span>
                EN VIVO
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between text-[9px] text-foreground-400">
        <span className="flex items-center gap-1">
          <i className="ri-calendar-line text-[8px]"></i>
          {'favoritedAtRelative' in item ? (item as any).favoritedAtRelative : ''}
        </span>
        <span className="flex items-center gap-1">
          <i className="ri-heart-fill text-[8px] text-rose-400"></i>
          {'score' in item ? (item as any).score : ''}
        </span>
      </div>
    </Link>
  );
}

function getSubtitle(item: FavoriteItem): string {
  switch (item.favoriteType) {
    case 'radio': return `${(item as FavoriteRadio).genre} · ${(item as FavoriteRadio).location}`;
    case 'programa': return `${(item as FavoritePrograma).station} · ${(item as FavoritePrograma).host}`;
    case 'locutor': return `${(item as FavoriteLocutor).station}`;
    case 'evento': return `${(item as FavoriteEvento).dateRelative} · ${(item as FavoriteEvento).location}`;
    case 'concurso': return `Cierra: ${(item as FavoriteConcurso).endDateRelative} · ${(item as FavoriteConcurso).prize.split('+')[0]}`;
    case 'club': return `${(item as FavoriteClub).members.toLocaleString()} miembros · ${(item as FavoriteClub).clubType}`;
    default: return '';
  }
}

function isOnAir(item: FavoriteItem): boolean {
  if (item.favoriteType === 'radio') return (item as FavoriteRadio).isLive;
  if (item.favoriteType === 'programa') return (item as FavoritePrograma).onAir;
  if (item.favoriteType === 'locutor') return (item as FavoriteLocutor).onAir;
  return false;
}